import { Button } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { DayEventRow } from 'src/Components/Drawers/DayDetailDrawer/DayEventRow';
import useFetchEvents, { LocalEvent } from 'src/Hooks/Queries/useFetchEvents';
import { useCanCreate } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { EventDtoOriginEnum } from 'src/Services/API/generated';
import { trackPlanningDayDetailOpened } from 'src/Services/Mixpanel/events';

import styles from './DayDetailDrawer.module.scss';

const DAY_FORMAT = 'YYYY-MM-DD';

const DayDetailDrawer = () => {
  const { t, i18n } = useTranslation();
  const { assetId, date: dateParam } = useParams<{ assetId: string; date: string }>();
  const navigate = useNavigate();
  const { hasPermission: canCreate } = useCanCreate('event');
  const assetAboveQuotas = useIsAssetAboveQuotas();

  const parsedDay = useMemo(() => dayjs(dateParam ?? '', DAY_FORMAT, true), [dateParam]);
  const isValidDate = parsedDay.isValid();

  // Fallback to today when the URL param is invalid so hook calls stay stable;
  // we short-circuit with a redirect right after.
  const day = isValidDate ? parsedDay : dayjs();

  // Use the same window as PlanningPage so the react-query cache is shared.
  const { data: events } = useFetchEvents({
    startDate: day.startOf('month').add(-1, 'week'),
    endDate: day.endOf('month').add(1, 'week'),
  });

  const dayEvents = useMemo<LocalEvent[]>(() => {
    if (!events || !isValidDate) return [];
    const dayStart = day.startOf('day');
    const dayEnd = day.endOf('day');
    return events
      .filter(event => {
        const start = event.startTime;
        const end = event.endTime ?? start;
        return start.isBefore(dayEnd) && end.isAfter(dayStart);
      })
      .sort((a, b) => {
        const aMulti = Boolean(a.endTime && !a.endTime.isSame(a.startTime, 'day'));
        const bMulti = Boolean(b.endTime && !b.endTime.isSame(b.startTime, 'day'));
        if (aMulti !== bMulti) return aMulti ? -1 : 1;
        return a.startTime.diff(b.startTime);
      });
  }, [events, day, isValidDate]);

  // Track open once per drawer mount (matches "1 seule fois par ouverture" spec).
  const eventsCount = dayEvents.length;
  useEffect(() => {
    if (!isValidDate) return;
    trackPlanningDayDetailOpened({ events_count: eventsCount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isValidDate) {
    return <Navigate to={`/asset/${assetId}/planning`} replace />;
  }

  const handleEventClick = (event: LocalEvent) => {
    const occurrenceDate = event.isOccurrence ? event.startTime : undefined;
    const search = occurrenceDate ? `?startTime=${occurrenceDate.toISOString()}` : '';
    if (event.origin === EventDtoOriginEnum.Event) {
      navigate(`../event/${event.id}${search}`);
    } else if (event.origin === EventDtoOriginEnum.PracticalInformation) {
      navigate(`../practical-information/${event.id}`);
    } else if (event.origin === EventDtoOriginEnum.Project) {
      navigate(`../project/${event.id}`);
    } else if (event.origin === EventDtoOriginEnum.Task) {
      navigate(`../task/${event.id}`);
    }
    // External: read-only, no navigation target.
  };

  const handleCreateClick = () => {
    navigate('../new-event', { state: { defaultDate: day.toDate().getTime() } });
  };

  const title = day.locale(i18n.language.toLowerCase()).format('dddd D MMMM YYYY');

  return (
    <AnimatedRoutedDrawer title={title} to={`/asset/${assetId}/planning`}>
      {() => (
        <div className={styles.container}>
          {dayEvents.length === 0 ? (
            <div className={styles.emptyState}>
              {t('planning.dayDetail.empty', 'Aucun événement ce jour-là')}
            </div>
          ) : (
            <div className={styles.eventsList}>
              {dayEvents.map(event => {
                const isNavigable = event.origin !== EventDtoOriginEnum.External;
                return (
                  <DayEventRow
                    key={event.localId}
                    event={event}
                    locale={i18n.language}
                    onClick={isNavigable ? () => handleEventClick(event) : undefined}
                  />
                );
              })}
            </div>
          )}

          {canCreate && !assetAboveQuotas && (
            <Button
              fullWidth
              leftSection={<IconPlus size={18} />}
              onClick={handleCreateClick}
              className={styles.createButton}
            >
              {t('planning.dayDetail.createEvent', 'Nouvel événement à cette date')}
            </Button>
          )}
        </div>
      )}
    </AnimatedRoutedDrawer>
  );
};

export default DayDetailDrawer;
